
/* eslint-disable max-len */
import * as EventEmitter from 'src/shared/eventEmitter/EventEmitter';

import { AaasContentFormatTracker } from './AaasContentFormatTracker';
import { CanvasRestApi } from './CanvasRestApi';
import * as AllyConfig from './Config';
import { IframeRestApi } from './IframeRestApi';
import { LearnRestApi } from './LearnRestApi';
import { MoodleRestApi } from './MoodleRestApi';
import { ProcessTracker } from './ProcessTracker';


// Gets set by the `init` function
let internalRestApi = null;

/**
 * Initialize the Facade
 */
export function init() {
    switch (AllyConfig.lmsType()) {
        case 'canvas': {
            internalRestApi = new CanvasRestApi();
            break;
        }
        case 'learn': {
            internalRestApi = new LearnRestApi();
            break;
        }
        case 'moodle': {
            internalRestApi = new MoodleRestApi();
            break;
        }
        case 'iframe': {
            internalRestApi = new IframeRestApi();
            break;
        }
        default: {
            throw new Error(`Unsupported LMS type specified: ${ AllyConfig.lmsType()}`);
        }
    }
}

export function getRestApi() {
    if (internalRestApi !== null) {
        return internalRestApi;
    } else {
        throw new Error('Rest API was not initialized yet. Have you called .init()?');
    }
}

/**
 * Get the file reports for a set of file references.
 *
 * @param {FileReference[]} fileReferences The file reference objects indicating how the data is described
 * @param {Object} [opts] Optional arguments
 * @param {Boolean} [opts.forceInstructorFeedbackEnabled] Whether or not instructor feedback should be returned,
 *                                                         regardless of the server-side configuration for
 *                                                         instructor feedback enablement in this course
 * @param {Function} callback Standard callback method
 * @param {Object} callback.err Standard error object
 * @param {Object} callback.data The data that's returned by the file reports endpoint
 * @param {Object[]} callback.data.uploadsReport An array of the same length of `fileReferences`, containing either
 *                                               the file info or `null` if there is no score for the file
 * @param {Object} callback.data.flags A set of (client-specific) flags
 */
export function getFileInformation(
    fileReferences,
    opts,
    callback
) {
    opts = opts || {};
    const fileReferenceData = fileReferences.map(function(fileReference) {
        return fileReference.data();
    });
    const encodedCourseId = encodeURIComponent(AllyConfig.courseId());
    let url = `/api/v1/${ AllyConfig.clientId() }/reports/courses/${ encodedCourseId }/content`;
    if (opts.forceInstructorFeedbackEnabled === true || opts.forceInstructorFeedbackEnabled === false) {
        url += `?forceInstructorFeedbackEnabled=${ opts.forceInstructorFeedbackEnabled}`;
    }

    internalRestApi.http(
        'POST',
        url,
        JSON.stringify(fileReferenceData),
        { 'contentType': 'application/json' },
        function(err, response) {
            if (err) {
                return callback(err);
            }

            const uploadsReport = response.uploadsReport.map(function(fileInfo) {
                if (fileInfo && fileInfo.libraryReference !== null) {
                    fileInfo.libraryReference = toJsonSafe(fileInfo.libraryReference);
                }

                return fileInfo;
            });
            const data = {
                'uploadsReport': uploadsReport,
                'flags': response.flags
            };
            return callback(null, data);
        });
}

/**
 * Generate HTML feedback for the AXE results.
 *
 * @param {string} html The AXE results for which to generate feedback
 * @param {function} callback Standard callback function
 */
export function generateAxeFeedback(axeResults, callback) {
    internalRestApi.http(
        'POST',
        `/api/v1/${AllyConfig.clientId()}/content/generateFeedback/axe`,
        JSON.stringify(axeResults),
        { 'contentType': 'application/json' },
        callback
    );
}

/**
 * Retrieves client configuration.
 */
export function getClientInfo() {
    return new Promise((resolve, reject) => {
        const data = {
            'courseId': encodeURIComponent(AllyConfig.courseId())
        };
        internalRestApi.http(
            'GET',
            `/api/v1/${AllyConfig.clientId()}`,
            data,
            {},
            (err, response) => {
                if (!err) {
                    resolve(response);
                } else {
                    reject(err);
                }
            }
        );
    });
}

/**
 * Fetch WYSIWYG content
 * @param externalId FileId/RichContentId of file/content to fetch
 * @param uploadType Whether it's File or RichContent
 */
export function fetchContentBody(externalId, uploadType) {
    const encodedExternalId = encodeURIComponent(externalId);
    const data = {
        'uploadType': encodeURIComponent(uploadType)
    };
    return new Promise((resolve, reject) => {
        internalRestApi.http(
            'GET',
            `/api/v1/${AllyConfig.clientId()}/content/course/${AllyConfig.courseId()}/content/${encodedExternalId}`,
            data,
            {},
            (err, response) => {
                if (!err) {
                    resolve(response);
                } else {
                    reject(err);
                }
            }
        );
    });
}

/**  Get the ImmersiveReader launch token. */
export function getImmersiveReaderAuth() {
    return new Promise((resolve, reject) => {
        internalRestApi.http(
            'GET',
            `/api/v1/${AllyConfig.clientId()}/azure/immersiveReader/authenticate`,
            null,
            {},
            (err, response) => {
                if (!err) {
                    resolve(response);
                } else {
                    reject(err);
                }
            }
        );
    });
}

/**
 * Get the file report for a single file reference.
 *
 * @param {FileReference} fileReference The file reference object indicating how the data is described
 * @param {Object} [opts] Optional arguments
 * @param {String} [opts.uploadType] The upload type to specify for the resource whose report to get. Should be one of
 *                                   'File' or 'RichContent'. Default: 'File'
 * @param {Boolean} [opts.feedback] Whether or not instructor feedback should be returned, regardless of the server-side
 *                                  configuration for instructor feedback enablement in this course
 * @param {Boolean} [opts.formats] Indicates whether the formats information needs to be returned
 * @param {Function} callback Standard callback method
 */
export function getIndividualFileReport(fileReference, opts, callback) {
    opts = opts || {};
    const encodedCourseId = encodeURIComponent(AllyConfig.courseId());
    const encodedFileId = encodeURIComponent(fileReference.fileId);
    var path = `/api/v2/clients/${ AllyConfig.clientId() }/courses/${ encodedCourseId }/files/${
        encodedFileId }/report`;

    const params = [];
    if (typeof opts.uploadType === 'string') {
        params.push(`uploadType=${opts.uploadType}`);
    }
    if (typeof opts.feedback === 'boolean') {
        params.push(`feedback=${opts.feedback}`);
    }
    if (typeof opts.formats === 'boolean') {
        params.push(`formats=${opts.formats}`);
    }
    if (fileReference.embed !== undefined) {
        const embed = encodeURIComponent(JSON.stringify(fileReference.embed));
        params.push(`embed=${embed}`);
    }
    const url = `${path }?${ params.join('&')}`;
    internalRestApi.http('GET', url, null, {}, function(err, report) {
        if (!err && report && report.metadata && typeof report.metadata.libraryReference === 'string') {
            try {
                report.metadata.libraryReference = JSON.parse(report.metadata.libraryReference);
            } catch (err) {
                // Just swallow ther error and continue
            }
        }

        return callback(err, report);
    });
}

/**
 * Get the file report for a single rich content item.
 *
 * @param {String} richContentId The rich content id
 * @param {Object} [opts] Optional arguments
 * @param {Boolean} [opts.feedback] Whether or not instructor feedback should be returned, regardless of the server-side
 *                                  configuration for instructor feedback enablement in this course
 * @param {Boolean} [opts.formats] Indicates whether the formats information needs to be returned
 * @param {Function} callback Standard callback method
 */
export function getIndividualRichContentReport(richContentId, opts, callback) {
    opts = opts || {};
    const encodedCourseId = encodeURIComponent(AllyConfig.courseId());
    const encodedRichContentId = encodeURIComponent(richContentId);
    var path = `/api/v2/clients/${ AllyConfig.clientId() }/courses/${ encodedCourseId }/rich-content/${ encodedRichContentId }/report`;

    const params = [];
    if (typeof opts.feedback === 'boolean') {
        params.push(`feedback=${opts.feedback}`);
    }
    if (typeof opts.formats === 'boolean') {
        params.push(`formats=${opts.formats}`);
    }
    const url = `${path }?${ params.join('&')}`;
    internalRestApi.http('GET', url, null, {}, callback);
}

/**
 * Get the file report for an Ally as a Service content item
 *
 * @param {String} contentHash The content hash
 * @param {Object} [opts] Optional arguments
 * @param {Boolean} [opts.feedback] Whether or not instructor feedback should be returned, regardless of the server-side
 *                                  configuration for instructor feedback enablement in this course
 * @param {Boolean} [opts.formats] Indicates whether the formats information needs to be returned
 * @param {Function} callback Standard callback method
 */
export function getAaasContentReport(contentHash, opts, callback) {
    opts = opts || {};
    var path = `/api/v2/clients/${ AllyConfig.clientId() }/content/${ contentHash}`;

    const params = [];
    if (typeof opts.feedback === 'boolean') {
        params.push(`feedback=${opts.feedback}`);
    }
    if (typeof opts.formats === 'boolean') {
        params.push(`formats=${opts.formats}`);
    }
    const url = `${path }?${ params.join('&')}`;
    internalRestApi.http('GET', url, null, {}, function(err, report) {
        if (!err && report && report.metadata && typeof report.metadata.libraryReference === 'string') {
            try {
                report.metadata.libraryReference = JSON.parse(report.metadata.libraryReference);
            } catch (err) {
                // Just swallow ther error and continue
            }
        }

        return callback(err, report);
    });
}

/** Get the course report for the currently configurated course */
export function getCourseReport(callback) {
    var encodedCourseId = encodeURIComponent(AllyConfig.courseId());
    var path = `/api/v1/${ AllyConfig.clientId() }/reports/courses/${ encodedCourseId}`;
    internalRestApi.http('GET', path, null, {}, callback);
}

/**
 * Get the embed information for a file
 *
 * @param  {UploadType} uploadType The upload type of the content
 * @param  {String}     externalId The id of the upload for which to get the embed information
 * @param  {Function}   callback Standard callback method
 * @param  {Object}     callback.err Standard error object
 * @param  {Object}     callback.embedInfo The embed information for the file
 */
export function getEmbedInfo(uploadType, externalId, callback) {
    const encodedCourseId = encodeURIComponent(AllyConfig.courseId());
    const uploadTypePathParth = (uploadType === 'RichContent') ? 'rich-content' : 'files';
    const encodedExternalId = encodeURIComponent(externalId);
    const path = `/api/v1/${AllyConfig.clientId()}/courses/${encodedCourseId}/${uploadTypePathParth}/${encodedExternalId}/embedInfo`;
    internalRestApi.http('GET', path, null, {}, callback);
}

/**
 * Enable the alternative formats for a file or rich content
 *
 * @param {String} uploadType The type of content
 * @param {String} externalId The id of the file or rich content to enable the alternative formats for
 * @param {Function} callback Standard callback method
 * @param {Object} callback.err Standard error object
 */
export function enableAlternativeFormats(uploadType, externalId, callback) {
    const encodedCourseId = encodeURIComponent(AllyConfig.courseId());
    const encodedExternalId = encodeURIComponent(externalId);
    const uploadTypePath = (uploadType === 'RichContent') ? 'rich-content' : 'files';
    const path = [
        '/api/v1',
        AllyConfig.clientId(),
        'courses',
        encodedCourseId,
        uploadTypePath,
        encodedExternalId,
        'enableAlternativeFormats'
    ].join('/');
    internalRestApi.http('POST', path, null, {}, callback);
}

/**
 * Disable the alternative formats for a file or rich content
 *
 * @param {String} uploadType The type of content
 * @param {String} externalId The id of the file or rich content to disable the alternative formats for
 * @param {Function} callback Standard callback method
 * @param {Object} callback.err Standard error object
 */
export function disableAlternativeFormats(uploadType, externalId, callback) {
    const encodedCourseId = encodeURIComponent(AllyConfig.courseId());
    const encodedExternalId = encodeURIComponent(externalId);
    const uploadTypePath = (uploadType === 'RichContent') ? 'rich-content' : 'files';
    const path = [
        '/api/v1',
        AllyConfig.clientId(),
        'courses',
        encodedCourseId,
        uploadTypePath,
        encodedExternalId,
        'disableAlternativeFormats'
    ].join('/');
    internalRestApi.http('POST', path, null, {}, callback);
}

/**
 * Fetches course content details for a specific course and content ID.
 *
 * @param {String} courseId - The ID of the course for which content details are being fetched.
 * @param {String} contentId - The ID of the content within the course.
 * @param {Function} callback - A callback function to handle the response or error.
 */
export function getCourseContentDetails(courseId, contentId, callback) {
    const path = [
        '/api/v1',
        AllyConfig.clientId(),
        'content',
        'courses',
        courseId,
        'contents',
        contentId,
        'details'
    ].join('/');

    internalRestApi.http('GET', path, null, {}, (error, data) => {
        if (error) {
            return callback(error);
        }

        callback(null, data);
    });
}

/**
 * Get the webpage profile from the local webpage data.
 *
 * @param  {Webpage} webpage The scraped local webpage information
 * @param  {Function} callback Standard callback method
 * @param  {Object} [callback.err] Standard error object
 * @param  {Object} [callback.webpageProfile] The webpage profile response
 */
export function getWebpageProfile(webpage, callback) {
    var path = `/api/v1/${ AllyConfig.clientId() }/webpageProfile`;
    internalRestApi.http(
        'POST',
        path,
        JSON.stringify({ 'webpage': webpage }),
        { 'contentType': 'application/json' },
        callback
    );
}

/**
 * Get the associated files for a rich content item
 *
 * @param {String} richContentId The id of the rich content item to get the associated files for
 * @param {Object} basicAssociatedFileReferences The associated files that were picked up by the UI
 */
export function getRichContentAssociatedFiles(richContentId, basicAssociatedFileReferences, callback) {
    basicAssociatedFileReferences = basicAssociatedFileReferences || [];
    const encodedCourseId = encodeURIComponent(AllyConfig.courseId());
    const encodedRichContentId = encodeURIComponent(richContentId);
    const path = [
        '/api/v2/clients',
        AllyConfig.clientId(),
        'courses',
        encodedCourseId,
        'rich-content',
        encodedRichContentId,
        'files'
    ].join('/');
    internalRestApi.http(
        'POST',
        path,
        JSON.stringify(basicAssociatedFileReferences),
        { 'contentType': 'application/json' },
        callback
    );
}

/**
 * Get a URL where given file can be downloaded from. It's assumed that the user already has an authenticated
 * session with the returned URL or the URL contains enough information to authenticate the request.
 *
 * @param  {String}     fileId                  The id of the file to get a download URL for
 * @param  {Function}   callback                Standard callback method
 * @param  {Object}     callback.err            Standard error object
 * @param  {String}     callback.downloadUrl    The URL where the file can be downloaded from
 */
export function getDownloadUrl(fileId, callback) {
    var encodedCourseId = encodeURIComponent(AllyConfig.courseId());
    var encodedFileId = encodeURIComponent(fileId);
    var path = `/api/v1/${ AllyConfig.clientId() }/courses/${ encodedCourseId }/files/${ encodedFileId }/download`;
    var opts = {
        'headers': {
            'Accept': 'application/json'
        }
    };
    internalRestApi.http('GET', path, null, opts, function(err, data) {
        if (err) {
            return callback(err);
        }

        if (data && data.url) {
            return callback(null, data.url);
        } else {
            return callback({ 'code': 404, 'msg': 'Missing download url' });
        }
    });
}

/**
 * Save the metadata for a file
 *
 * @param  {String}     fileId                         The id of the file to save the metadata for
 * @param  {Object}     update                         The updates to persist. At least 1 should be specified
 * @param  {GeneratedAlternativeText}     [update.description]           The description to save
 * @param  {String}     [update.libraryReference]      The library reference to save
 */
export function saveMetadata(fileId, update, callback) {
    var encodedCourseId = encodeURIComponent(AllyConfig.courseId());
    var encodedFileId = encodeURIComponent(fileId);
    var path = `/api/v1/${ AllyConfig.clientId() }/courses/${ encodedCourseId }/files/${ encodedFileId}`;
    internalRestApi.http('POST', path, update, null, function(err, data) {
        if (err) {
            return callback(err);
        }

        // Parse the libary reference, if any
        if (data.libraryReference && typeof data.libraryReference === 'string') {
            data.libraryReference = toJsonSafe(data.libraryReference);
        }

        return callback(null, data);
    });
}

/**
 * Update the alt text of an embedded image in a rich content item.
 *
 * @param  {String}     richContentId   The id of the rich content item being updated
 * @param  {String}     fileId          The id of the file referenced in the rich content item being updated
 * @param  {String}     locator         The locator (e.g., CSS selector) of the file reference in the rich content item
 * @param  {Number}     index           The index of the file reference among the `locator`s matched elements
 * @param  {String}     newAltText      The new alternative text being applied
 * @param  {Function}   callback        Standard callback method
 * @param  {Object}     callback.err    Standard error object
 */
export function updateRichContentAltText(richContentId, fileId, locator, index, newAltText, callback) {
    var encodedCourseId = encodeURIComponent(AllyConfig.courseId());
    var path = `/api/v1/${ AllyConfig.clientId() }/courses/${ encodedCourseId }/rich-content/${
        richContentId }/alt-text`;
    var data = {
        'fileId': fileId,
        'locator': locator,
        'index': index,
        'newAltText': newAltText
    };
    internalRestApi.http('POST', path, data, null, callback);
}

/**
 * Remove the alt text of an embedded image in a rich content item.
 *
 * @param  {String}     richContentId   The id of the rich content item being updated
 * @param  {String}     fileId          The id of the file referenced in the rich content item being updated
 * @param  {String}     locator         The locator (e.g., CSS selector) of the file reference in the rich content item
 * @param  {Number}     index           The index of the file reference among the `locator`s matched elements
 * @param  {Function}   callback        Standard callback method
 * @param  {Object}     callback.err    Standard error object
 */
export function removeRichContentAltText(richContentId, fileId, locator, index, callback) {
    var encodedCourseId = encodeURIComponent(AllyConfig.courseId());
    var path = `/api/v1/${ AllyConfig.clientId() }/courses/${ encodedCourseId }/rich-content/${
        richContentId }/alt-text`;
    var data = {
        'fileId': fileId,
        'locator': locator,
        'index': index
    };
    internalRestApi.http('DELETE', path, data, null, callback);
}

/**
 * Delete a file from the course in context
 *
 * @param  {String}     fileId          The id of file to delete from the course
 * @param  {Function}   callback        Standard callback method
 * @param  {Object}     callback.err    Standard error object
 */
export function deleteFile(fileId, callback) {
    var encodedCourseId = encodeURIComponent(AllyConfig.courseId());
    var path = `/api/v1/${ AllyConfig.clientId() }/courses/${ encodedCourseId }/files/${ fileId}`;
    internalRestApi.http('DELETE', path, {}, null, callback);
}

/**
 * Determine what alternative formats are available for a file that Ally could download from the public web. This is
 * potentially a deferred reuqest wherein Ally accepts the request and provides a process id that can be polled
 * until completion.
 *
 * @param  {Object} download How to download the resource to compute its alternative formats list
 * @param  {String} download.url A URL that the server can publicly download
 * @param  {Number} progressInterval How often to check back to the server on progress
 * @param  {Function} callback Standard callback function
 * @param  {Object} [callback.err] An error object, if any
 * @param  {String} [callback.err.code] The HTTP-like error code
 * @param  {String} [callback.err.msg] The human-readable message explaining the error
 * @param  {String} [callback.err.errorType] If the list failed to generate, this value expresses the error type
 * @param  {Object} [callback.response] A response that indicates either an immediately available URL or a deferred request
 * @param  {String[]} [callback.response.formats] An immediately available list of formats, if applicable
 * @param  {EventEmitter} [callback.response.emitter] An emitter tracking processing status, if applicable
 * @param  {Function} [callback.response.emitter.cancel] Cancels the progress tracker. When cancelled, a 'cancel' event is fired and no further events will fire
 */
export function listAvailableFormats(download, progressInterval, callback) {
    var path = `/api/v1/${ AllyConfig.clientId() }/content/listAvailableFormats`;
    var data = JSON.stringify({ 'download': download });
    var opts = { 'contentType': 'application/json' };

    internalRestApi.http('POST', path, data, opts, function(err, init) {
        if (err) {
            return callback(err);
        } else if (init.status === 'Failed') {
            // The process failed. Pass along the failure type and its message
            return callback({ 'msg': init.message, 'errorType': init.errorType });
        } else if (init.status && progressInterval) {
            // The emitter that the consumer can use to track this deferred response
            var emitter = new EventEmitter();

            // We have a deferred response that we were expecting could be possible. Bind to a process tracker and
            // complete the response when it finishes
            var tracker = new ProcessTracker(init.process_id || init.processId, progressInterval);

            // Proxy progress to the consumer emitter
            tracker.on('progress', function(status) {
                emitter.emit('progress', status);
            });

            // Proxy cancel requests on the emitter down to the tracker
            emitter.cancel = function() {
                tracker.cancel();
            };

            // Proxy cancelled events to the consumer emitter
            tracker.on('cancelled', function() {
                emitter.emit('cancelled');
            });

            // Perform one more request to the AAF endpoint as that might carry more error information
            tracker.on('error', function() {
                retrieveFinalListAvailableFormatsInformation(download, emitter);
            });

            // When finished, re-fetch the alternative format and carry forward its result
            tracker.on('finished', function() {
                retrieveFinalListAvailableFormatsInformation(download, emitter);
            });

            // The response will be the emitter since we do not have a URL available yet
            return callback(null, { 'emitter': emitter });
        } else if (init.status) {
            // We have a deferred response, but we were expecting an immediate one. This is weird
            return callback({ 'code': 500, 'msg': 'Expected immediate response' });
        } else if (init.formats) {
            // URL is immediately available, yay!
            return callback(null, {
                'formats': init.formats,
                'libraryReference': init.libraryReference,
                'newAlternativeFormats': init.newAlternativeFormats
            });
        } else {
            // This is weird. We must have had some unexpected response format
            return callback({ 'code': 500, 'msg': 'Unexpected response (no status and no available formats)' });
        }
    });
}

/**
 * Download an alternative version. The potential emitter response provides the following events:
 *
 *  * `progress(status)`    - Status from another polling interval of the processing status. Invoked multiple times throughout processing
 *  * `finished(url)`       - Emitted at most 1 time upon successful completion. If it fails, this is not invoked, instead "error" is invoked
 *  * `error(err)`          - Emitted at most 1 time in the event either processing fails or the tracker fails to poll the processing endpoint
 *  * `cancelled`           - Emitted at most 1 time in the event the tracker emitter is cancelled
 *
 * @param  {Object}         download                            The download strategy for the file (would either have key `url` or `lms`)
 * @param  {String}         [download.url]                      The preauthenticated URL of the download
 * @param  {Object}         [download.lms]                      The LMS download strategy info
 * @param  {Object}         [download.lms.courseId]             The id of the course
 * @param  {Object}         [download.lms.externalId]           The external id (i.e., file id)
 * @param  {Object}         [download.lms.revisionId]           The revision id, if applicable
 * @param  {String}         [download.lms.contentHash]          A sha-256 hash of the rich content body. This can be used to help perform authorization checking as it proves the user has access to the content
 * @param  {String}         formatName                          Which alternative format to download (e.g., Html, Braille, etc..)
 * @param  {String}         [formatParam]                       The additional AF parameter, e.g language of the format (only applicable for format name 'Translation') or title (only applicable for format name 'Tts' and upload type 'RichContent')
 * @param  {Boolean}        asAttachment                        Whether the Content-Disposition header should be added
 * @param  {Number}         [progressInterval]                  Amount of time in milliseconds to hold off between progress checks to the server. If falsey, we expect the alternative version to be made available immediately
 * @param  {Function}       callback                            Standard callback function
 * @param  {Object}         callback.err                        An error object, if any
 * @param  {String}         [callback.err.code]                 The HTTP-like error code
 * @param  {String}         [callback.err.msg]                  The human-readable message explaining the error
 * @param  {String}         [callback.err.errorType]            When the AAF failed to generate, this value expresses the error type
 * @param  {Object}         callback.response                   A response that indicates either an immediately available URL or a deferred request. If immediate, there will be a URL on the response, if deferred there will an emitter providing progress information
 * @param  {String}         [callback.response.url]             An immediately available download URL, if applicable
 * @param  {EventEmitter}   [callback.response.emitter]         An emitter tracking processing status, if applicable
 * @param  {Function}       [callback.response.emitter.cancel]  Cancels the progress tracker. When cancelled, a 'cancel' event is fired and no further events will fire
 */
export function downloadAlternativeVersion(download, formatName, formatParam, asAttachment, progressInterval, callback, attempts, emitter) {
    attempts = attempts || 1;
    if (attempts > 3) {
        return callback({ 'msg': 'Surpassed maximum number of process deferals', 'errorType': 'generic' });
    }

    var method = null;
    var path = `/api/v1/${ AllyConfig.clientId()}`;
    var data = null;
    var opts = null;
    if (download.url) {
        method = 'POST';
        path += '/content/convert';
        data = JSON.stringify({
            'asAttachment': asAttachment,
            'download': download,
            'format': {
                'name': formatName,
                'options': {
                    'formatParam': formatParam
                }
            },
            'acceptTermsOfUse': true
        });
        opts = { 'contentType': 'application/json' };
    } else {
        const courseId = encodeURIComponent(download.lms.courseId);
        method = 'GET';
        const externalId = encodeURIComponent(download.lms.externalId);
        if (download.lms.uploadType === 'RichContent') {
            path += `/courses/${ courseId }/rich-content/${ externalId }/formats/${ formatName}`;
        } else {
            path += `/formats/${ courseId }/${ externalId }/${ formatName}`;
        }
        data = {
            'acceptTOU': true,
            'formatParam': formatParam,
            'asAttachment': asAttachment,
            'contentHash': download.lms.contentHash
        };
        opts = null;
    }

    internalRestApi.http(method, path, data, opts, function(err, init) {
        if (err) {
            return callback(err);
        } else if (init.status === 'Failed') {
            // The process failed. Pass along the failure type and its message
            return callback({ 'msg': init.message, 'errorType': init.errorType });
        } else if (init.status && progressInterval) {
            // The emitter that the consumer can use to track this deferred response
            emitter = emitter || new EventEmitter();

            // We have a deferred response that we were expecting could be possible. Bind to a process tracker and
            // complete the response when it finishes
            var tracker = new ProcessTracker(init.processId, progressInterval);

            // Proxy progress to the consumer emitter
            tracker.on('progress', function(status) {
                emitter.emit('progress', status);
            });

            // Proxy cancel requests on the emitter down to the tracker
            emitter.cancel = function() {
                tracker.cancel();
            };

            // Proxy cancelled events to the consumer emitter
            tracker.on('cancelled', function() {
                emitter.emit('cancelled');
            });

            // Perform one more request to the AAF endpoint as that might carry more error information
            tracker.on('error', function() {
                retrieveFinalAafInformation(download, formatName, formatParam, asAttachment, progressInterval, attempts, emitter);
            });

            // When finished, re-fetch the alternative format and carry forward its result
            tracker.on('finished', function() {
                retrieveFinalAafInformation(download, formatName, formatParam, asAttachment, progressInterval, attempts, emitter);
            });

            // The response will be the emitter since we do not have a URL available yet
            return callback(null, { 'emitter': emitter });
        } else if (init.status) {
            // We have a deferred response, but we were expecting an immediate one. This is weird
            return callback({ 'code': 500, 'msg': 'Expected immediate response' });
        } else if (init.url) {
            // URL is immediately available, yay!
            return callback(null, { 'url': init.url });
        } else {
            // This is weird. We must have had some unexpected response format
            return callback({ 'code': 500, 'msg': 'Unexpected response (no status and no url)' });
        }
    });
}

/**
 * Download an alternative version for an Ally as a Serice content item.
 *
 * The potential emitter response provides the following events:
 *
 *  * `progress(status)`    - Status from another polling interval of the processing status. Invoked multiple times throughout processing
 *  * `finished(url)`       - Emitted at most 1 time upon successful completion. If it fails, this is not invoked, instead "error" is invoked
 *  * `error(err)`          - Emitted at most 1 time in the event either processing fails or the tracker fails to poll the processing endpoint
 *  * `cancelled`           - Emitted at most 1 time in the event the tracker emitter is cancelled
 *
 * @param  {String}         contentHash The content hash of the item for which a format needs to be generated
 * @param  {String}         url  The preauthenticated url where the source content item can be downloaded
 * @param  {String}         formatName                          Which alternative format to download (e.g., Html, Braille, etc..)
 * @param  {String}         [formatParam]                       The additional AF parameter, e.g language of the format (only applicable for format name 'Translation') or title (only applicable for format name 'Tts' and upload type 'RichContent')
 * @param  {Boolean}        asAttachment                        Whether the Content-Disposition header should be added
 * @param  {Number}         [progressInterval]                  Amount of time in milliseconds to hold off between progress checks to the server. If falsey, we expect the alternative version to be made available immediately
 * @param  {Function}       callback                            Standard callback function
 * @param  {Object}         callback.err                        An error object, if any
 * @param  {String}         [callback.err.code]                 The HTTP-like error code
 * @param  {String}         [callback.err.msg]                  The human-readable message explaining the error
 * @param  {String}         [callback.err.errorType]            When the AAF failed to generate, this value expresses the error type
 * @param  {Object}         callback.response                   A response that indicates either an immediately available URL or a deferred request. If immediate, there will be a URL on the response, if deferred there will an emitter providing progress information
 * @param  {String}         [callback.response.url]             An immediately available download URL, if applicable
 * @param  {EventEmitter}   [callback.response.emitter]         An emitter tracking processing status, if applicable
 * @param  {Function}       [callback.response.emitter.cancel]  Cancels the progress tracker. When cancelled, a 'cancel' event is fired and no further events will fire
 */
export function downloadAaasAlternativeVersion(contentHash, downloadUrl, formatName, formatParam, asAttachment, progressInterval, callback, attempts, emitter) {
    attempts = attempts || 1;
    if (attempts > 3) {
        return callback({ 'msg': 'Surpassed maximum number of process deferals', 'errorType': 'generic' });
    }

    getAaasToken(function(err, token) {
        if (err) {
            return callback(err);
        }

        requestAaasContentFormat(contentHash, downloadUrl, formatName, formatParam, function(err, init) {
            if (err) {
                return callback(err);
            } else if (init.status === 'Failed') {
                // The process failed. Pass along the failure type and its message
                return callback({ 'msg': init.error.message, 'errorType': init.error.type });
            } else if (init.downloadUrl) {
                // URL is immediately available, yay!
                return getAaasDownloadUrlWithToken(init.downloadUrl, asAttachment, function(err, signedUrl) {
                    if (err) {
                        return callback({ 'msg': 'Unable to retrieve JWT token' });
                    }

                    return callback(null, { 'url': signedUrl });
                });
            } else if (init.status && progressInterval) {
                // The emitter that the consumer can use to track this deferred response
                emitter = emitter || new EventEmitter();

                // We have a deferred response. Keep polling the /status endpoint
                var tracker = new AaasContentFormatTracker(init.statusUrl, token);

                // Proxy progress to the consumer emitter
                tracker.on('progress', function(status) {
                    emitter.emit('progress', status);
                });

                // Proxy cancel requests on the emitter down to the tracker
                emitter.cancel = function() {
                    tracker.cancel();
                };

                // Proxy cancelled events to the consumer emitter
                tracker.on('cancelled', function() {
                    emitter.emit('cancelled');
                });

                // Perform one more request to the AAF endpoint as that might carry more error information
                tracker.on('error', function() {
                    retrieveFinalAaasAafInformation(init.statusUrl, asAttachment, emitter);
                });

                // When finished, re-fetch the alternative format and carry forward its result
                tracker.on('finished', function() {
                    retrieveFinalAaasAafInformation(init.statusUrl, asAttachment, emitter);
                });

                // The response will be the emitter since we do not have a URL available yet
                return callback(null, { 'emitter': emitter });
            } else if (init.status) {
                // We have a deferred response, but we were expecting an immediate one. This is weird
                return callback({ 'code': 500, 'msg': 'Expected immediate response' });
            } else {
                // This is weird. We must have had some unexpected response format
                return callback({ 'code': 500, 'msg': 'Unexpected response (no status and no url)' });
            }
        });
    });
}

/** Request an alt text string for an image where we have a client/course/file id and language. File id is passed in the request and language is passed as a query paramater */
export function getAIAltTextForStandaloneImage(fileId, lang, context=null) {
    const url = `/api/v2/clients/${AllyConfig.clientId()}/courses/${AllyConfig.courseId()}/files/${fileId}/captions/suggestion?lang=${lang}`;
    const body = {};
    if (context) {
        body['context'] = context;
    }
    return new Promise((resolve, reject) => {
        internalRestApi.http(
            'POST',
            url,
            JSON.stringify(body),
            { 'contentType': 'application/json' },
            (err, data) => {
                if (err) {
                    reject(err);
                }
                resolve(data);
            }
        );
    });
}

/** Request an alt text string for an image where we have a client/course/file id and language. File ID is passed as a parameter and language is passed as a query parameter.*/
export function getAIAltTextForWYSIWYGImage(fileId, lang, context=null) {
    const url = `/api/v2/clients/${AllyConfig.clientId()}/courses/${AllyConfig.courseId()}/captions/suggestion?lang=${lang}`;
    const body = { 'source': fileId };
    if (context) {
        body['context'] = context;
    }
    return new Promise((resolve, reject) => {
        internalRestApi.http(
            'POST',
            url,
            JSON.stringify(body),
            { 'contentType': 'application/json' },
            (err, data) => {
                if (err) {
                    reject(err);
                }
                resolve(data);
            });
    });
}

/** Request the given format to be generated for the given content item */
function requestAaasContentFormat(contentHash, downloadUrl, formatName, formatParam, callback) {
    var method = 'POST';
    var path = `/api/v2/clients/${ AllyConfig.clientId() }/content/${ contentHash }/formats/${ formatName}`;
    var data = {
        'formatParam': formatParam,
        'url': downloadUrl
    };
    internalRestApi.http(method, path, data, null, callback);
}

/** Fetch the /status information one last time and send the appropriate response on the emitter */
function retrieveFinalAaasAafInformation(statusUrl, asAttachment, emitter) {
    internalRestApi.http('GET', statusUrl, null, null, function(err, data) {
        if (err) {
            // Our final request for the alternative format failed, push the error forward
            return emitter.emit('error', err);
        } else if (data.status === 'failed') {
            // No alternative format could be generated, propagate the error
            return emitter.emit('error', { 'msg': data.error.message, 'errorType': data.error.type });
        } else if (data.status === 'success') {
            // The alternative format is ready, sign the download URL with the JWT token so that it can be downloaded in
            // the browser and emit it
            return getAaasDownloadUrlWithToken(data.downloadUrl, asAttachment, function(err, signedUrl) {
                if (err) {
                    return emitter.emit('error', err);
                }

                emitter.emit('finished', signedUrl);
            });
        }
    });
}

/**
 * Get a URL through with a format can be downloaded with all authentication embedded onto it.
 *
 * i.e. stick the JWT token in the query string
 */
function getAaasDownloadUrlWithToken(downloadUrl, asAttachment, callback) {
    getAaasToken(function(err, token) {
        if (err) {
            return callback(err);
        }

        const signedUrl = `${AllyConfig.baseUrl()}${downloadUrl}?asAttachment=${asAttachment}&jwt=${token}`;
        return callback(null, signedUrl);
    });
}

/** Get the JWT token that powers the Aaas integration */
function getAaasToken(callback) {
    internalRestApi.sign('GET', '/', null, function(err, data) {
        if (err) {
            return callback(err);
        }

        const token = data.headers.Authorization.split(' ').pop();
        return callback(null, token);
    });
}

export function trackEvent(name, payload, callback) {
    callback = callback || function() {};
    var path = `/api/v1/${ AllyConfig.clientId() }/events`;
    var data = {
        'name': name,
        'properties': payload
    };
    internalRestApi.http('POST', path, JSON.stringify(data), { 'contentType': 'application/json' }, callback);
}

export function getHelp(queryParams, callback) {
    var path = `/api/v1/${ AllyConfig.clientId() }/help`;
    internalRestApi.http('GET', path, queryParams, null, callback);
}

/**
 * Send a request for instructor feedback help.
 *
 * @param  {String}     fileId          The id of the file the user is viewing in instructor feedback
 * @param  {String}     fromPageUrl     The source page URL in the LMS where the user entered instructor feedback
 * @param  {String}     currentIssue    The instructor feedback issue the user is viewing
 * @param  {String}     [message]       The message the user sent, if any
 * @param  {Function}   [callback]      Standard callback function
 */
export function sendInstructorFeedbackExpertHelp(fileId, fromPageUrl, currentIssue, message, callback) {
    callback = callback || {};
    var encodedCourseId = encodeURIComponent(AllyConfig.courseId());
    var encodedFileId = encodeURIComponent(fileId);
    var path = `/api/v1/${ AllyConfig.clientId() }/courses/${ encodedCourseId }/files/${ encodedFileId
        }/help/sendExpertInstructorFeedbackHelp`;
    var data = {
        'currentIssue': currentIssue,
        'fromPageUrl': fromPageUrl
    };

    // Set a message only if there is content available
    if (message) {
        data.message = message;
    }

    internalRestApi.http('POST', path, data, null, callback);
}

/**
 * Send a request for alternative formats help.
 *
 * @param {Object} file The file info object
 * @param {String} file.id The id of the file the user is trying to view alternative formats for
 * @param {String} [file.name] The name of the file, as extracted from the UI
 * @param {String} [file.type] The type of the file, as extracted from the UI (e.g., html-page, pdf, presentation)
 * @param {String} [file.url] The url of the file, as extracted from the UI
 * @param {String} fromPageUrl The source page URL in the LMS where the user entered alternative formats
 * @param {String} currentFormat The selected format
 * @param {String} [message] The message the user sent, if any
 * @param {Function} [callback] Standard callback function
 */
export function sendAlternativeFormatsExpertHelp(
    file,
    fromPageUrl,
    currentFormat,
    message,
    callback
) {
    callback = callback || {};
    var encodedCourseId = encodeURIComponent(AllyConfig.courseId());
    var encodedFileId = encodeURIComponent(file.id);
    var path = `/api/v1/${ AllyConfig.clientId() }/courses/${ encodedCourseId }/files/${ encodedFileId
        }/help/sendExpertAlternativeFormatsHelp`;
    var data = {
        'currentFormat': currentFormat,
        'fromPageUrl': fromPageUrl
    };

    // Set the file name, only if it was specified
    if (file.name) {
        data.fileName = file.name;
    }

    // Set the file type, only if it was specified
    if (file.type) {
        data.fileType = file.type;
    }

    // Set the file url, only if it was specified
    if (file.url) {
        data.fileUrl = file.url;
    }

    // Set a message only if there is content available
    if (message) {
        data.message = message;
    }

    internalRestApi.http('POST', path, data, null, callback);
}

/**
 * Get the status of a feature flag.
 *
 * @param {String}      flagName    The name of the flag to check
 * @param {Function}    callback    Standard callback function
 */
export function checkFeatureFlag(flagName, callback) {
    const path = `/api/v1/${AllyConfig.clientId()}/feature/${flagName}`;
    internalRestApi.http('GET', path, {}, {}, callback);
}

/**
 * Get the status of a feature flag within course.
 *
 * @param {String}      flagName    The name of the flag to check
 * @param {String}      courseId    The id of the course to check the flag for
 * @param {Function}    callback    Standard callback function
 */
export function checkCourseFeatureFlag(flagName, courseId, callback) {
    const path = `/api/v1/${AllyConfig.clientId()}/course/${courseId}/feature/${flagName}`;
    internalRestApi.http('GET', path, {}, {}, callback);
}

function retrieveFinalAafInformation(download, formatName, formatParam, asAttachment, progressInterval, attempts, emitter) {
    // No progress interval is provided here to indicate we only care about an immediate response, and
    // we'll get an error if that happens. When we get our expected URL, we can pass it through to the
    // emitter that is in the hands of the consumer
    downloadAlternativeVersion(download, formatName, formatParam, asAttachment, progressInterval, function(err, maybeAvailable) {
        if (err) {
            // Our final request for the alternative format failed, push the error forward
            return emitter.emit('error', err);
        } else if (maybeAvailable.url) {
            // We have our response now, we can emit a finish.
            return emitter.emit('finished', maybeAvailable.url);
        }

        // If we did not fail or get an immediate response, we are just going through another deferred processing
        // stage so we need to pass on the emitter, request and potentially wait again
    }, attempts + 1, emitter);
}

function retrieveFinalListAvailableFormatsInformation(download, emitter) {
    listAvailableFormats(download, null, function(err, response) {
        if (err) {
            return emitter.emit('error', err);
        } else if (response.formats) {
            const data = {
                'formats': response.formats,
                'libraryReference': response.libraryReference,
                'newAlternativeFormats': response.newAlternativeFormats
            };
            return emitter.emit('finished', data);
        }
    });
}

/**
 * Safely parse the given string to JSON. If the string can't be parsed, null will be returned
 *
 * @param  {String} [str] The string to parse
 * @return {Object | null} The parsed string or `null` if the string could not be parsed
 */
function toJsonSafe(str) {
    if (str) {
        try {
            return JSON.parse(str);
        } catch (err) {
            return null;
        }
    }
    return null;
}
